import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { Blade, Button, DataGrid } from "rhombus-react";
import { fetchAssignedCreatives } from "reducers/actions/ContentFeedActions";
import { AssignedCreatives } from "reducers/AssignedCreatives";
import { useGridScrollUtils } from "utils/DataGrid";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";
import { DEFAULT_ROW_HEIGHT } from "utils/constants";
import ProgressContainer from "widgets/ProgressContainer";
import { TOGGLE_AUTO_UPDATES_COLUMNS } from "./ToggleAutoUpdatesColumns";

import "./ToggleAutoUpdatesModal.css";

const MAX_VISIBLE_ROWS = 8;

function calculateGridDimensions(actualRowsCount, predictedRowsCount) {
	const rowsCountForHeight = actualRowsCount || predictedRowsCount;
	const visibleRowsCount = Math.min(rowsCountForHeight, MAX_VISIBLE_ROWS);
	const gridHeight = DEFAULT_ROW_HEIGHT + (visibleRowsCount * DEFAULT_ROW_HEIGHT);
	const hasScroll = rowsCountForHeight > MAX_VISIBLE_ROWS;

	return {
		actualRowsCount,
		gridHeight,
		hasScroll,
		rowsCountForHeight,
	};
}

export default function ContentFeedConfirmModal({
	onClose,
	confirmLabel,
	confirmingLabel,
	buildTitle,
	onConfirm,
}) {
	const { contentFeedId } = useParams();
	const location = useLocation();
	const contentFeedName = location.state?.contentFeedName;
	const creativeCount = location.state?.creativeCount || 0;
	const dispatch = useDispatch();
	const [isConfirming, setIsConfirming] = React.useState(false);

	const assignedCreatives = useSelector(state => state.assignedCreatives?.items || []);
	const isLoading = useSelector(state => state.assignedCreatives?.isLoading || false);
	const dataGridRef = useGridScrollUtils([]);

	useTooltipRebuild(assignedCreatives);

	useEffect(() => {
		if (contentFeedId && creativeCount > 0) {
			dispatch(fetchAssignedCreatives(contentFeedId));
		}
		return () => {
			dispatch(AssignedCreatives.clearAssignedCreatives());
		};
	}, [contentFeedId]);

	const mappedCreatives = useMemo(() => {
		return assignedCreatives.map(creative => ({
			...creative,
			contentFeedId,
			contentFeedName,
		}));
	}, [assignedCreatives, contentFeedId, contentFeedName]);

	function getRow(rowID) {
		if (rowID === -1) {
			return;
		}
		return mappedCreatives[rowID];
	}

	function handleConfirm() {
		setIsConfirming(true);
		onConfirm(contentFeedId).finally(() => {
			setIsConfirming(false);
			onClose();
		});
	}

	function renderCreativesGrid() {
		if (creativeCount === 0) {
			return null;
		}

		const { actualRowsCount, gridHeight, hasScroll, rowsCountForHeight } = calculateGridDimensions(
			mappedCreatives.length,
			creativeCount
		);

		const dataGridProps = {
			columns: TOGGLE_AUTO_UPDATES_COLUMNS,
			rowGetter: getRow,
			rowsCount: actualRowsCount,
			rowHeight: DEFAULT_ROW_HEIGHT,
			enableCellAutoFocus: false,
			...(rowsCountForHeight > 0 && { minHeight: gridHeight }),
			...(hasScroll && { maxHeight: gridHeight }),
		};

		const containerClassName = `toggle-auto-updates-grid-container${hasScroll ? " has-scroll" : ""}`;

		return (
			<ProgressContainer isLoading={isLoading}>
				<div ref={dataGridRef} className={containerClassName}>
					<DataGrid {...dataGridProps} />
				</div>
			</ProgressContainer>
		);
	}

	function renderContent() {
		return (
			<div className="toggle-auto-updates-body">
				<p className="toggle-auto-updates-subtitle">
					This will affect all <strong>{creativeCount}</strong> assigned creatives.
				</p>
				{renderCreativesGrid()}
			</div>
		);
	}

	function renderFooter() {
		return [
			<Button key="cancel" theme="default" onClick={onClose} disabled={isConfirming}>
				Cancel
			</Button>,
			<Button key="confirm" theme="blue" onClick={handleConfirm} disabled={isConfirming}>
				{isConfirming ? confirmingLabel : confirmLabel}
			</Button>,
		];
	}

	return (
		<Blade
			type="modal"
			title={buildTitle(contentFeedName)}
			className="toggle-auto-updates-modal"
			allowFullScreen={false}
			bladeProps={{ verticallyCenter: true }}
			xClose
			onClose={onClose}
			renderContent={renderContent}
			renderFooter={renderFooter}
			setAppTitle={false}
		/>
	);
}

ContentFeedConfirmModal.displayName = "ContentFeedConfirmModal";

ContentFeedConfirmModal.propTypes = {
	onClose: PropTypes.func.isRequired,
	confirmLabel: PropTypes.string.isRequired,
	confirmingLabel: PropTypes.string.isRequired,
	buildTitle: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
};
